/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { CiCdDto } from '../models/ci-cd-dto';
import { AttachDetachCiRequest } from '../models/attach-detach-ci-request';
import { PushCredentialsResponse } from '../models/push-credentials-response';
import { PushCredentialsRequest } from '../models/push-credentials-request';
import { SaasArtifactRequest } from '../models/saas-artifact-request';

/**
 * Ui Ci Cd Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiCiCdControllerService extends __BaseService {
  static readonly saveCiCdDetailsUsingPOSTPath = '/cc-ui/v1/ci-cd';
  static readonly attachRuleAndWorkflowUsingPUTPath = '/cc-ui/v1/ci-cd/attach';
  static readonly detachRuleAndWorkflowUsingPUTPath = '/cc-ui/v1/ci-cd/detach';
  static readonly generateArtifactPushCredentialsUsingPOST1Path =
    '/cc-ui/v1/ci-cd/pushCredentials';
  static readonly registerArtifactSaasUsingPOST1Path =
    '/cc-ui/v1/ci-cd/register';
  static readonly getCiCdDetailsUsingGETPath = '/cc-ui/v1/ci-cd/{stackName}';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Save CI/CD Details
   *
   * - **Description:** Saves CI/CD configuration details.
   * - **Restrictions:** Cannot change registration type after initial configuration.
   * - **Permissions:** Requires `CI_CD_CONFIGURE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param ciCdDto ciCdDto
   */
  saveCiCdDetailsUsingPOSTResponse(
    ciCdDto: CiCdDto,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = ciCdDto;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/ci-cd`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * Save CI/CD Details
   *
   * - **Description:** Saves CI/CD configuration details.
   * - **Restrictions:** Cannot change registration type after initial configuration.
   * - **Permissions:** Requires `CI_CD_CONFIGURE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param ciCdDto ciCdDto
   */
  saveCiCdDetailsUsingPOST(ciCdDto: CiCdDto): __Observable<null> {
    return this.saveCiCdDetailsUsingPOSTResponse(ciCdDto).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * Attach Rule and Workflow
   *
   * - **Description:** Attaches a rule and workflow to an artifact CI.
   * - **Restrictions:** CI must exist or will be created for the project.
   * - **Permissions:** Requires `ARTIFACT_CI_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param attachDetachCiRequest attachDetachCiRequest
   */
  attachRuleAndWorkflowUsingPUTResponse(
    attachDetachCiRequest: AttachDetachCiRequest,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = attachDetachCiRequest;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/cc-ui/v1/ci-cd/attach`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * Attach Rule and Workflow
   *
   * - **Description:** Attaches a rule and workflow to an artifact CI.
   * - **Restrictions:** CI must exist or will be created for the project.
   * - **Permissions:** Requires `ARTIFACT_CI_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param attachDetachCiRequest attachDetachCiRequest
   */
  attachRuleAndWorkflowUsingPUT(
    attachDetachCiRequest: AttachDetachCiRequest,
  ): __Observable<null> {
    return this.attachRuleAndWorkflowUsingPUTResponse(
      attachDetachCiRequest,
    ).pipe(__map((_r) => _r.body as null));
  }

  /**
   * Detach Rule and Workflow
   *
   * - **Description:** Detaches a rule and workflow from an artifact CI.
   * - **Restrictions:** Existing CI registration required.
   * - **Permissions:** Requires `ARTIFACT_CI_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param attachDetachCiRequest attachDetachCiRequest
   */
  detachRuleAndWorkflowUsingPUTResponse(
    attachDetachCiRequest: AttachDetachCiRequest,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = attachDetachCiRequest;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/cc-ui/v1/ci-cd/detach`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * Detach Rule and Workflow
   *
   * - **Description:** Detaches a rule and workflow from an artifact CI.
   * - **Restrictions:** Existing CI registration required.
   * - **Permissions:** Requires `ARTIFACT_CI_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param attachDetachCiRequest attachDetachCiRequest
   */
  detachRuleAndWorkflowUsingPUT(
    attachDetachCiRequest: AttachDetachCiRequest,
  ): __Observable<null> {
    return this.detachRuleAndWorkflowUsingPUTResponse(
      attachDetachCiRequest,
    ).pipe(__map((_r) => _r.body as null));
  }

  /**
   * Generate Artifact Push Credentials
   *
   * - **Description:** Generates push credentials for an artifact.
   * - **Restrictions:** Only for supported artifactory types.
   * - **Permissions:** Requires `ARTIFACTS_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param pushCredentialsRequest pushCredentialsRequest
   * @return Credentials generated successfully.
   */
  generateArtifactPushCredentialsUsingPOST1Response(
    pushCredentialsRequest: PushCredentialsRequest,
  ): __Observable<__StrictHttpResponse<PushCredentialsResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = pushCredentialsRequest;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/ci-cd/pushCredentials`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<PushCredentialsResponse>;
      }),
    );
  }
  /**
   * Generate Artifact Push Credentials
   *
   * - **Description:** Generates push credentials for an artifact.
   * - **Restrictions:** Only for supported artifactory types.
   * - **Permissions:** Requires `ARTIFACTS_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param pushCredentialsRequest pushCredentialsRequest
   * @return Credentials generated successfully.
   */
  generateArtifactPushCredentialsUsingPOST1(
    pushCredentialsRequest: PushCredentialsRequest,
  ): __Observable<PushCredentialsResponse> {
    return this.generateArtifactPushCredentialsUsingPOST1Response(
      pushCredentialsRequest,
    ).pipe(__map((_r) => _r.body as PushCredentialsResponse));
  }

  /**
   * Register Artifact SaaS
   *
   * - **Description:** Registers a new artifact within the system for SaaS.
   * - **Restrictions:** Valid artifactory name and consistent registration type required. Blueprint must be CI/CD configured.
   * - **Permissions:** Requires `ARTIFACTS_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param artifactRequest artifactRequest
   */
  registerArtifactSaasUsingPOST1Response(
    artifactRequest: SaasArtifactRequest,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = artifactRequest;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/ci-cd/register`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * Register Artifact SaaS
   *
   * - **Description:** Registers a new artifact within the system for SaaS.
   * - **Restrictions:** Valid artifactory name and consistent registration type required. Blueprint must be CI/CD configured.
   * - **Permissions:** Requires `ARTIFACTS_WRITE` permission.
   * - **Audit Logging:** Not applicable.
   * @param artifactRequest artifactRequest
   */
  registerArtifactSaasUsingPOST1(
    artifactRequest: SaasArtifactRequest,
  ): __Observable<null> {
    return this.registerArtifactSaasUsingPOST1Response(artifactRequest).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * Get CI/CD Details
   *
   * - **Description:** Retrieves CI/CD details for a specified project name.
   * - **Restrictions:** Requires existing default artifact routing rule or promotion workflow.
   * - **Permissions:** None required.
   * - **Audit Logging:** Not applicable.
   * @param stackName stackName
   * @return Successfully retrieved CI/CD details.
   */
  getCiCdDetailsUsingGETResponse(
    stackName: string,
  ): __Observable<__StrictHttpResponse<CiCdDto>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/ci-cd/${encodeURIComponent(String(stackName))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<CiCdDto>;
      }),
    );
  }
  /**
   * Get CI/CD Details
   *
   * - **Description:** Retrieves CI/CD details for a specified project name.
   * - **Restrictions:** Requires existing default artifact routing rule or promotion workflow.
   * - **Permissions:** None required.
   * - **Audit Logging:** Not applicable.
   * @param stackName stackName
   * @return Successfully retrieved CI/CD details.
   */
  getCiCdDetailsUsingGET(stackName: string): __Observable<CiCdDto> {
    return this.getCiCdDetailsUsingGETResponse(stackName).pipe(
      __map((_r) => _r.body as CiCdDto),
    );
  }
}

module UiCiCdControllerService {}

export { UiCiCdControllerService };
